import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../../../context/AuthContext';

export default function InviteMemberModal({ onClose, onSuccess }) {
  const { auth } = useContext(AuthContext);

  // Role selection: "Foreman" or "Project Manager"
  const [role, setRole] = useState('Foreman'); // default is Foreman
  const [inviteName, setInviteName] = useState(''); // for both PM or Foreman
  const [email, setEmail] = useState('');

  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [isInviting, setIsInviting] = useState(false);

  // For Foreman role: searching Ironworker
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newIronworker, setNewIronworker] = useState({
    Name: '',
    Email: '',
    'Union ID': '',
  });

  // Reset form fields when role changes
  useEffect(() => {
    setEmail('');
    setInviteName('');
    setSearchTerm('');
    setSearchResults([]);
    setShowCreateForm(false);
  }, [role]);

  // For Foreman, dynamic search
  const handleSearch = useCallback(async () => {
    if (role !== 'Foreman') return;
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }
    try {
      const resp = await fetch(
        `/api/myteconstruction/ironworkers/search?term=${encodeURIComponent(searchTerm)}`,
        {
          headers: { Authorization: `Bearer ${auth.token}` },
        }
      );
      const data = await resp.json();
      setSearchResults(data);
    } catch (err) {
      console.error('Search error:', err);
    }
  }, [searchTerm, auth.token, role]);

  useEffect(() => {
    if (role === 'Foreman') {
      handleSearch();
    }
  }, [handleSearch, role]);

  // If user picks an Ironworker from search results
  const handleSelectIronworker = (iw) => {
    setEmail(iw.Email || '');
    setInviteName(iw.Name || ''); // auto-populate name from Ironworker
    setSearchTerm('');
    setSearchResults([]);
  };

  // Create brand-new Ironworker
  const handleCreateIronworker = async () => {
    try {
      setError('');
      setSuccessMsg('');

      const resp = await fetch('/api/myteconstruction/ironworkers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.token}`,
        },
        body: JSON.stringify(newIronworker),
      });
      if (!resp.ok) {
        const errData = await resp.json();
        throw new Error(errData.error || 'Failed to create ironworker');
      }
      const created = await resp.json();
      setEmail(created.Email || '');
      setInviteName(created.Name || '');
      setShowCreateForm(false);
      setSuccessMsg('Ironworker created successfully. You can now invite them.');
    } catch (err) {
      console.error(err);
      setError(err.message || 'Error creating ironworker');
    }
  };

  // Send invite
  const handleSendInvite = async () => {
    setError('');
    setSuccessMsg('');

    // Validate
    if (role === 'Foreman') {
      if (!email.trim()) {
        setError('Please select or create an ironworker from the list first.');
        return;
      }
      // We also have inviteName from the Ironworker
    } else {
      // Project Manager
      if (!inviteName.trim() || !email.trim()) {
        setError('Please enter both name and email for the project manager.');
        return;
      }
    }

    setIsInviting(true);
    try {
      // Include "name" for Project Manager or the name from ironworker (Foreman)
      const payload = {
        email,
        role,
        name: inviteName || ''
      };

      const res = await fetch('/api/myteconstruction/invitations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorData = await res.json();
        setError(errorData.error || 'Invitation failed.');
      } else {
        setSuccessMsg('Invitation sent successfully.');
        if (onSuccess) onSuccess();
      }
    } catch (err) {
      console.error('Network or server error:', err);
      setError('Network or server error.');
    } finally {
      setIsInviting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 p-6 rounded-lg shadow-lg w-full max-w-md relative">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-white"
        >
          &times;
        </button>

        <h3 className="text-lg font-medium mb-4 text-white">Invite Team Member</h3>

        {/* Role Selection */}
        <div className="mb-4">
          <label className="block text-sm text-gray-300 mb-1">Select Role</label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="w-full p-2 rounded-md bg-gray-700 text-white"
          >
            <option value="Foreman">Foreman</option>
            <option value="Project Manager">Project Manager</option>
          </select>
        </div>

        {error && <div className="text-red-500 mb-2">{error}</div>}
        {successMsg && <div className="text-green-500 mb-2">{successMsg}</div>}

        {role === 'Foreman' && (
          <>
            {/* Search or create Ironworker */}
            <div className="mb-4">
              <label className="block text-sm text-gray-300 mb-1">
                Search Existing Ironworker
              </label>
              <input
                type="text"
                className="w-full p-2 rounded-md bg-gray-700 text-white"
                placeholder="Type a name, email, or Union ID"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchResults.length > 0 && (
                <ul className="bg-gray-700 mt-1 max-h-48 overflow-y-auto rounded-md">
                  {searchResults.map((iw) => (
                    <li
                      key={iw._id}
                      className="px-3 py-2 hover:bg-gray-600 cursor-pointer text-sm text-white"
                      onClick={() => handleSelectIronworker(iw)}
                    >
                      {iw.Name} | {iw.Email} | {iw['Union ID']}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <button
              onClick={() => setShowCreateForm(!showCreateForm)}
              className="mb-4 px-3 py-1 bg-green-600 hover:bg-green-700 text-sm rounded-md"
            >
              {showCreateForm ? 'Cancel Create Ironworker' : 'Create New Ironworker'}
            </button>

            {showCreateForm && (
              <div className="bg-gray-700 p-3 rounded-md mb-4">
                <label className="block text-sm mb-1 text-gray-300">Name *</label>
                <input
                  type="text"
                  className="w-full p-2 rounded-md bg-gray-800 text-white mb-2"
                  value={newIronworker.Name}
                  onChange={(e) =>
                    setNewIronworker({ ...newIronworker, Name: e.target.value })
                  }
                />
                <label className="block text-sm mb-1 text-gray-300">Email *</label>
                <input
                  type="email"
                  className="w-full p-2 rounded-md bg-gray-800 text-white mb-2"
                  value={newIronworker.Email}
                  onChange={(e) =>
                    setNewIronworker({ ...newIronworker, Email: e.target.value })
                  }
                />
                <label className="block text-sm mb-1 text-gray-300">Union ID *</label>
                <input
                  type="text"
                  className="w-full p-2 rounded-md bg-gray-800 text-white mb-4"
                  value={newIronworker['Union ID']}
                  onChange={(e) =>
                    setNewIronworker({ ...newIronworker, 'Union ID': e.target.value })
                  }
                />
                <button
                  onClick={handleCreateIronworker}
                  className="bg-blue-600 hover:bg-blue-700 px-3 py-1 rounded-md"
                >
                  Save Ironworker
                </button>
              </div>
            )}

            {/* Display fields that are auto-filled from Ironworker selection */}
            <div className="mb-4">
              <label className="block text-sm text-gray-300 mb-1">Selected Ironworker Email</label>
              <input
                type="email"
                className="w-full p-2 rounded-md bg-gray-700 text-white"
                placeholder="Email from Ironworker"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm text-gray-300 mb-1">Ironworker Name</label>
              <input
                type="text"
                className="w-full p-2 rounded-md bg-gray-700 text-white"
                placeholder="Name from Ironworker"
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
              />
            </div>
          </>
        )}

        {role === 'Project Manager' && (
          <>
            <div className="mb-4">
              <label className="block text-sm text-gray-300 mb-1">Name</label>
              <input
                type="text"
                className="w-full p-2 rounded-md bg-gray-700 text-white"
                placeholder="Enter full name"
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm text-gray-300 mb-1">Email</label>
              <input
                type="email"
                className="w-full p-2 rounded-md bg-gray-700 text-white"
                placeholder="Enter email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          </>
        )}

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 py-1 bg-gray-600 hover:bg-gray-700 rounded-md text-white"
          >
            Cancel
          </button>
          <button
            onClick={handleSendInvite}
            disabled={isInviting}
            className="px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded-md text-white flex items-center"
          >
            {isInviting ? (
              <>
                <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  ></path>
                </svg>
                Processing...
              </>
            ) : role === 'Foreman' ? 'Invite Foreman' : 'Invite Project Manager'}
          </button>
        </div>
      </div>
    </div>
  );
}
